/**
 * Test builds: "Publish for testing" queues an artifact build on the running
 * forge; a READY build opens through the production render path with
 * `?channel=test` (owner-only, isolated tenant) in a new tab.
 */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, Hammer, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { ForgeTestBuildSummary } from '../types';

/** The URL a test build renders at — the SAME page as production, plus the channel. */
export function testBuildUrl(archeId: string): string {
  return `/a/${archeId}?channel=test`;
}

interface ForgeTestBuildsProps {
  archeId: string;
  builds: ForgeTestBuildSummary[];
  readyBuild: ForgeTestBuildSummary | null;
  onBuild: () => void;
  /** The forge is running — a build needs a live runtime to package the workspace. */
  canBuild: boolean;
  isBuildRequested: boolean;
  /** Why a build is not possible right now; defaults to "start the forge". */
  disabledHint?: string;
}

const BUILD_STATUS_CLASSES: Record<ForgeTestBuildSummary['status'], string> = {
  building: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  ready: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  failed: 'bg-red-500/10 text-red-600 border-red-500/20',
};

export function ForgeTestBuilds({
  archeId,
  builds,
  readyBuild,
  onBuild,
  canBuild,
  isBuildRequested,
  disabledHint = 'Start the forge to publish a test version.',
}: ForgeTestBuildsProps) {
  const openTestBuild = () => {
    window.open(testBuildUrl(archeId), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="rounded-lg border p-3 space-y-3" data-testid="forge-test-builds">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Test builds</h3>
        <Button size="sm" onClick={onBuild} disabled={!canBuild || isBuildRequested}>
          {isBuildRequested ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <Hammer className="h-4 w-4 mr-1" />
          )}
          Publish for testing
        </Button>
      </div>
      {!canBuild && <p className="text-xs text-muted-foreground">{disabledHint}</p>}
      {readyBuild && (
        <Button size="sm" variant="outline" className="w-full" onClick={openTestBuild}>
          <ExternalLink className="h-4 w-4 mr-1" />
          Open test version
        </Button>
      )}
      {builds.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nothing published for testing yet.</p>
      ) : (
        <ul className="space-y-1.5 max-h-40 overflow-y-auto">
          {builds.slice(0, 8).map((b) => (
            <li key={b.id} className="flex items-center justify-between gap-2 text-xs">
              <span className="flex items-center gap-2 min-w-0">
                <Badge variant="outline" className={BUILD_STATUS_CLASSES[b.status]}>
                  {b.status}
                </Badge>
                <span className="text-muted-foreground truncate">
                  {b.createdAt
                    ? formatDistanceToNow(new Date(b.createdAt), { addSuffix: true })
                    : ''}
                </span>
              </span>
              {b.status === 'failed' && b.errorMessage && (
                <span className="text-destructive truncate" title={b.errorMessage}>
                  {b.errorMessage}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
